import type { Context, Entity, Evidence } from '../../types';
export type Lesson = Entity;
export interface TeachingObservation {
  uuid: string;
  lessonUuid: string;
  teacherMemberUuid: string;
  topic: string;
  text: string;
  evidence: Evidence;
  source: 'manual' | 'analysis';
  candidateUuid?: string;
  analysisTaskUuid?: string;
  status: 'candidate' | 'confirmed' | 'excluded';
  createdAt: string;
  confirmedAt?: string;
  confirmedBy?: string;
  reason?: string;
  planUuid?: string;
}
export interface Practice {
  uuid: string;
  planUuid: string;
  lessonUuid: string;
  evidence: Evidence;
  report: string;
  conditions: string;
  createdAt: string;
  submittedBy: string;
  accepted: boolean;
}
export interface MentorReview {
  uuid: string;
  practiceUuid: string;
  reviewer: string;
  at: string;
  accepted: boolean;
  note: string;
}
export interface ImprovementPlan {
  schema: 1;
  goal: string;
  topic: string;
  successCriteria: string;
  observationUuid?: string;
  sourceLessonUuid: string;
  evidence: Evidence;
  mentorMemberUuid: string;
  mentorName: string;
  scheduledLessonUuid?: string;
  practices: Practice[];
  reviews: MentorReview[];
  history: {
    uuid: string;
    at: string;
    action: string;
    actor: string;
    note: string;
  }[];
  taskUuid?: string;
  references: {
    knowledgeUuid: string;
    versionUuid: string;
    reuseUuid: string;
    title: string;
  }[];
}
export interface TeacherProfile {
  subject: string;
  topics: string[];
  mentorMemberUuid?: string;
  mentorName?: string;
  training: {
    uuid: string;
    title: string;
    at: string;
    feedback: string;
    sourceKnowledgeUuid?: string;
    versionUuid?: string;
  }[];
}
export interface AuthorizationScope {
  campuses: string[];
  roles: Context['role'][];
  memberUuids: string[];
  basis: string;
}
export interface KnowledgeVersion {
  uuid: string;
  knowledgeUuid: string;
  version: number;
  revision: number;
  author: string;
  createdAt: string;
  title: string;
  subject: string;
  stage: string;
  scene: string;
  topic: string;
  situation: string;
  steps: string;
  conditions: string;
  limitations: string;
  body: string;
  evidence: Evidence;
  sourceRevision: number;
  sourceConclusionUuid?: string;
  sourceConclusionRevision?: number;
  sourceVerified: boolean;
  redacted: boolean;
  scope: AuthorizationScope;
  status: 'draft' | 'submitted' | 'published' | 'returned' | 'withdrawn';
  reviewer?: string;
  reviewedAt?: string;
  reviewNote?: string;
}
export interface ReuseRecord {
  uuid: string;
  knowledgeUuid: string;
  versionUuid: string;
  targetUuid: string;
  targetMemberUuid?: string;
  kind: 'plan' | 'training';
  actor: string;
  at: string;
  feedback?: 'applicable' | 'notApplicable';
  note?: string;
}
export interface KnowledgeStore {
  drafts: Record<string, KnowledgeVersion>;
  versions: Record<string, KnowledgeVersion[]>;
  reuse: ReuseRecord[];
  favorites: { knowledgeUuid: string; actor: string }[];
  views: {
    knowledgeUuid: string;
    versionUuid: string;
    actor: string;
    at: string;
  }[];
}
export interface KnowledgeView {
  activity: { views: number; favorites: number; reuse: number };
  entity: Entity;
  published?: KnowledgeVersion;
  draft?: KnowledgeVersion;
  versions: KnowledgeVersion[];
  reuse: ReuseRecord[];
  favorite: boolean;
  canEdit: boolean;
  canReview: boolean;
  sourceAccessible: boolean;
  sourceChanged: boolean;
}
export interface LearningView {
  lessons: Entity[];
  recordings: Entity[];
  teachers: Entity[];
  plans: Entity[];
  observations: TeachingObservation[];
  candidates: {
    candidateUuid: string;
    jobUuid: string;
    text: string;
    topic: string;
    evidence: Evidence;
    memberUuid: string;
    campus: string;
  }[];
  metrics: {
    lessons: number;
    recordedLessons: number;
    confirmedObservations: number;
    pendingObservations: number;
    plans: number;
    completedPlans: number;
  };
}
export const planLabels: Record<string, string> = {
  draft: '待完善草稿',
  pending: '待接受',
  inProgress: '实践中',
  pendingFeedback: '待导师验收',
  pendingAcceptance: '待导师验收',
  completed: '已验收',
  cancelled: '已取消',
};
export const observationLabels = {
  candidate: '待教师确认',
  confirmed: '已确认观察',
  excluded: '已排除',
};
export const knowledgeLabels = {
  draft: '草稿',
  submitted: '待审核',
  pendingReview: '待审核',
  returned: '已退回',
  published: '已发布',
  withdrawn: '已撤回',
};
